import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { type CoMingleReturnProps, type ReturnShipperDisplay, toReturnShipper, withCoMingleReturns } from '@/pages/return/coMingle'
import { flushSync } from 'react-dom'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { callToAddImportLog } from '@/services/excel/apis'
import { failureError, invalidError, notice, rowInvalidError } from '@/components/pbo/notice'
import { FETCH_ALL_MESSAGES, guardFetching, noticeIfUploadExceedsPage } from '@/hooks/useFetchAllRows'
import { usePboQueryErrorToast } from '@/hooks/usePboQueryErrorToast'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, type ShipperScope, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { getShipperListV2 } from '@/services/shipper/apis'
import { ShipperItem } from '@/services/shipper/types'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import { apiErrMessage, downloadReturnMonitoringExcel } from '@/services/return/returnMonitoring/apis'
import {
  useGetReturnMonitoringItems,
  useSaveReturnMonitoring,
  useWithdrawReturnMonitoring,
} from '@/services/return/returnMonitoring/useReturnMonitoringQueries'
import {
  RETURN_MONITORING_DATE_TYPE_FILTER_FLAG,
  RETURN_MONITORING_PARTIAL_WITHDRAW_FLAG,
  ReturnMonitoringItemDto,
} from '@/services/return/returnMonitoring/types'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import ReturnMonitoringPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { isValidDownloadPassword } from './components/ExcelDownloadModal'
import { ExcelImportError, parseReturnMonitoringExcel } from './components/excelImport'
import { type RequiredField, ReturnMonitoringRow, useReturnMonitoringRows } from './hooks/useReturnMonitoringRows'
import {
  ACCEPTED_STATUS,
  ACT_FLAG_OPTIONS,
  ALL_STATUS_TAB,
  DATE_TYPE_OPTIONS,
  DEFAULT_DATE_TYPE,
  GRID_CDS,
  MESSAGES,
  REASON_DETAIL_MIN_LENGTH,
  STATUS_TABS,
  StatusTabKey,
} from './constants'
import { FIELD_NAMES, forceOwnShipper, hasRequiredFilter, ReturnMonitoringFormValues, toDateType, toReturnMonitoringReq } from './filterToRequest'
import { fromSearchQuery, ReturnMonitoringSearchQuery, toSearchQuery } from './searchQuery'

/**
 * RGR10 반품모니터링 — 화면(겉)은 컨덕터 생성본(`handoff/page.generated.tsx`)이고 이 파일은 배선이다(컴포넌트 모드).
 *
 * 옛 손코드 화면(`ReturnMonitoringPage` + `components/`)의 훅·API·검증을 그대로 슬롯에 잇는다. 편집 행 상태는 옛 훅
 * `useReturnMonitoringRows`(카멜케이스 DTO 행)가 계속 들고, 뷰에는 컬럼 키(UPPER_SNAKE)로 바꿔 넘긴다(toViewRow ↔ fromViewRow).
 *
 * 데모(정본)를 따라 옛 화면과 달라진 동작 — PR 본문에 적는다:
 *  · 상태 탭은 **받아 온 행을 거르는** 탭이다(건수 = 받은 행 기준). 그래서 조회는 상태 없이 한 번에 받고, 요약 API 는 부르지 않는다.
 *  · 페이지 크기 10,000(MOMS 공통) · [저장]·[반품철회]는 행을 골라야 눌린다.
 *  · 기준일(filters.rgr10DateType)·활성 상태 탭(onState.tab)은 생성본이 알려 준다(컨덕터 #696) — 다운로드 범위·URL 저장에 쓴다.
 *  · 반품수량은 업로드한 새 행에서만 편집 칸이다(컨덕터 #699 — 기존 행은 저장 대상이 아니다, D14).
 */

// ── 행 키 변환 ───────────────────────────────────────────────────────────────

/** 뷰 편집 셀 키 → 행 필드. 표시 전용 칸은 되돌리지 않는다(규칙 4). 출고센터·반품수량은 데모 편집 셀이지만 기존 행 저장 대상이 아니다(D14 — 신규 행만 rtnQty 를 싣는다). */
const EDITABLE_FIELDS = {
  ORG_INVOICE_NO: 'orgInvoiceNo',
  RTN_INVOICE_NO: 'rtnInvoiceNo',
  CLAIM_TYPE: 'claimType',
  CUSTOMER_CLAIM: 'customerClaim',
  CENTER_CLAIM: 'centerClaim',
  BARCODE: 'barcode',
  RTN_QTY: 'rtnQty',
  GI_WH_NM: 'giWhNm',
} as const satisfies Record<string, keyof ReturnMonitoringItemDto>

const EDITABLE_ENTRIES = Object.entries(EDITABLE_FIELDS) as [keyof typeof EDITABLE_FIELDS, keyof ReturnMonitoringItemDto][]

/**
 * [저장] 필수 검사 후보 — 저장되는 편집 칸(EDITABLE_COLUMNS + 신규 행 반품수량). 출고센터(GI_WH_NM)는 저장 대상이 아니라 뺐다.
 *  제목·기본 별표는 생성본 COLUMNS 와 같게 적는다 — 설정이 그 칸을 다루지 않으면 표 별표가 남으므로 검사도 같이 막는다(EST04·GR07 과 같은 규칙).
 *  필수 여부는 화면관리 그리드 설정이 정한다(LOGISPRD-17253). 1.0 RGR10 은 FilterBar.onSave → CommonUtil.onSaveData 가 막았다(FilterBar.js:649).
 */
export const REQUIRED_CANDIDATES: (core.CoreColumnType & { field: keyof ReturnMonitoringItemDto })[] = [
  { dataIndex: 'ORG_INVOICE_NO', title: '원송장번호', field: 'orgInvoiceNo', required: true },
  { dataIndex: 'RTN_INVOICE_NO', title: '반송번호', field: 'rtnInvoiceNo' },
  { dataIndex: 'CLAIM_TYPE', title: '클레임유형', field: 'claimType' },
  { dataIndex: 'CUSTOMER_CLAIM', title: '고객클레임 (비고)', field: 'customerClaim' },
  { dataIndex: 'CENTER_CLAIM', title: '비고사항(센터)', field: 'centerClaim' },
  { dataIndex: 'BARCODE', title: '바코드', field: 'barcode', required: true },
  { dataIndex: 'RTN_QTY', title: '반품수량', field: 'rtnQty', required: true },
]
const toRequiredFields = (columns: core.CoreColumnType[]): RequiredField[] =>
  columns.flatMap((column, i) => (column.required ? [{ key: REQUIRED_CANDIDATES[i].field, title: String(column.title) }] : []))

const asText = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '')
const asList = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .map(asText)
    .map((v) => v.trim())
    .filter(Boolean)

/** 편집 셀 값 → 행 필드 값. 수량은 숫자로, 빈 칸은 null. */
const toFieldValue = (field: keyof ReturnMonitoringItemDto, value: unknown): unknown => {
  if (field === 'rtnQty') {
    if (value === '' || value == null) return null
    const n = Number(value)
    return Number.isNaN(n) ? null : n
  }
  return value === '' || value == null ? null : asText(value)
}

/**
 * 행 → 뷰 행. key = 옛 훅 행키(BE id 기반 · 업로드 행 uuid) — 인덱스 key 면 재조회 뒤 체크가 다른 행에 붙는다(규칙 2).
 * 반송상태는 응답에 뒤 공백이 붙어 온다("접수완료 ") — 탭·태그가 완전일치로 보므로 벗긴다(LOGISPRD-12578).
 */
export const toViewRow = (row: ReturnMonitoringRow & ReturnShipperDisplay, coMingleEnabled = false): Row => ({
  key: row._rowKey,
  RECEIPT_DATE: row.receiptDate ?? '',
  RTN_DATE: row.rtnDate ?? '',
  ACT_FLAG: row.actFlag,
  RTN_WH_NM: row.rtnWhNm ?? '',
  ORD_STATUS: (row.ordStatus ?? '').trim(),
  ORG_INVOICE_NO: row.orgInvoiceNo ?? '',
  GI_ORD_NO: row.giOrdNo ?? '',
  GI_ORD_OPT_NO: row.giOrdOptNo ?? '',
  SPR_NM: (coMingleEnabled ? row.shipperName : null) ?? row.sprNm ?? '',
  RTN_INVOICE_NO: row.rtnInvoiceNo ?? '',
  CARRIER: row.carrier ?? '',
  CLAIM_TYPE: row.claimType ?? '',
  CUSTOMER_CLAIM: row.customerClaim ?? '',
  CENTER_CLAIM: row.centerClaim ?? '',
  BARCODE: row.barcode ?? '',
  SKU_ID: row.skuId ?? '',
  GDS_NM: row.gdsNm ?? '',
  GDS_OPT: row.gdsOpt ?? '',
  RTN_QTY: row.rtnQty ?? '',
  GI_WH_NM: row.giWhNm ?? '',
  // TODO(api): 입고센터(WH_NM) — 2.0 DTO 에 대응 필드가 없다(옛 화면 D8 과 같이 빈 칸).
  WH_NM: '',
  ACT_RETURN_DATE: row.actReturnDate ?? '',
  COMPANY_ORD_NO: row.companyOrdNo ?? '',
  COMPANY_ORD_OPT_NO: row.companyOrdOptNo ?? '',
  REF_ORD_NO: row.refOrdNo ?? '',
  REF_ORD_OPT_NO: row.refOrdOptNo ?? '',
  TRS_NM: row.trsNm ?? '',
  TRS_ADDR: row.trsAddr ?? '',
  TRS_PHONE: row.trsPhone ?? '',
  CLS_DATE: row.clsDate ?? '',
  BRD_NM: row.brdNm ?? '',
  GI_ACT_DATE: row.giActDate ?? '',
  GR_ACT_DATE: row.grActDate ?? '',
  INSPECT_DATE: row.inspectDate ?? '',
  INS_BY: row.insBy ?? '',
  INS_DATE: row.insDate ?? '',
  UPD_BY: row.updBy ?? '',
  ETC1: row.etc1 ?? '',
  RTN_EXC_DIV: row.rtnExcDiv ?? '',
  REC_START_DATE: row.recStartDate ?? '',
  // DS 표 계약 — 업로드 신규 행은 필수 빈 칸을 처음부터 빨갛게 표기한다.
  ...(row._isNew ? { __new: true } : {}),
})

/** 뷰 행(편집 셀 반영본) → 행. 편집 셀 키만 되돌린다 — 나머지는 원래 행 값을 지킨다. */
export const fromViewRow = (view: Row, base: ReturnMonitoringRow): ReturnMonitoringRow => {
  const next: Record<string, unknown> = { ...base }
  EDITABLE_ENTRIES.forEach(([viewKey, field]) => {
    if (viewKey in view) next[field] = toFieldValue(field, view[viewKey])
  })
  return next as unknown as ReturnMonitoringRow
}

const rowKeyOf = (row: Row): string => asText(row.key)

// ── 조회영역 값(뷰 filterKey) ↔ 옛 폼 값 ─────────────────────────────────────────

/** 뷰 조회영역 값 → 옛 폼 값. 기간은 뷰가 진입 때 기본 기간(최근 일주일)을 올려 준다 — 비어 있으면 필수 검사에서 막힌다. */
export const toFormValues = (filters: Record<string, unknown>): ReturnMonitoringFormValues => {
  const [from, to] = Array.isArray(filters.rgr10Date) ? (filters.rgr10Date as unknown[]).map(asText) : []
  return {
    [FIELD_NAMES.shipper]: asText(filters.SPR_NM),
    // 기준일 — 옵션 값이 조회 API dateType 코드다. 모르는 값은 접수일로(toDateType). FF off 면 요청 경계(toReturnMonitoringReq)가 접수일로 잠근다.
    [FIELD_NAMES.dateType]: toDateType(asText(filters.rgr10DateType) || undefined),
    [FIELD_NAMES.dateRange]: from && to ? [dayjs(from), dayjs(to)] : null,
    [FIELD_NAMES.rtnInvoiceNo]: asList(filters.RTN_INVOICE_NO),
    [FIELD_NAMES.actFlag]: asText(filters.ACT_FLAG) || undefined,
    [FIELD_NAMES.ordNo]: asList(filters.GI_ORD_NO),
  }
}

/** 옛 폼 값(URL 복원값) → 뷰 조회영역 값. 없는 날짜는 싣지 않는다(뷰가 기본 기간을 채운다). */
export const toFilterValues = (values: ReturnMonitoringFormValues): Record<string, unknown> => {
  const range = values[FIELD_NAMES.dateRange]
  const [from, to] = Array.isArray(range) ? range : [range, range]
  const rtnInvoiceNo = asList(values[FIELD_NAMES.rtnInvoiceNo])
  const ordNo = asList(values[FIELD_NAMES.ordNo])
  return {
    SPR_NM: values[FIELD_NAMES.shipper] ?? '',
    rgr10DateType: toDateType(values[FIELD_NAMES.dateType]),
    ...(from?.isValid() && to?.isValid() ? { rgr10Date: [from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')] } : {}),
    ...(rtnInvoiceNo.length ? { RTN_INVOICE_NO: rtnInvoiceNo } : {}),
    ACT_FLAG: values[FIELD_NAMES.actFlag] ?? '',
    ...(ordNo.length ? { GI_ORD_NO: ordNo } : {}),
  }
}

// ── 기타 ─────────────────────────────────────────────────────────────────────

/** 저장 확인창(PboConfirm) — 취소하면 throw(뷰는 실패로 보고 토스트 없이 선택을 그대로 둔다). */
const confirmOrThrow = (content: string): Promise<void> =>
  new Promise((resolve, reject) => {
    PboConfirm.show(content, {
      onConfirm: () => resolve(),
      onClose: () => reject(new Error('cancelled')),
    })
  })

/** 기준일 옵션 — 순서는 데모(정본)대로: 접수일(업로드)·입고완료일·도착일·검수완료일. 값 = 조회 API dateType 코드. */
const DATE_TYPE_VIEW_ORDER = ['RTN_DATE', 'GR_ACT_DATE', 'ACT_RETURN_DATE', 'INSPECT_DATE'] as const
const DATE_TYPE_VIEW_OPTIONS: Option[] = DATE_TYPE_VIEW_ORDER.map((v) => ({
  value: v,
  label: DATE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v,
}))
/** 뷰 상태 탭 key('all' = 전체) ↔ 옛 탭 key('전체'). 그 밖의 key 는 상태 문자열 그대로다. */
const VIEW_ALL_TAB = 'all'
const toStatusTab = (viewKey: string | undefined): StatusTabKey => STATUS_TABS.find((t) => t.key === viewKey)?.key ?? ALL_STATUS_TAB
const toViewTab = (tab: StatusTabKey): string => (tab === ALL_STATUS_TAB ? VIEW_ALL_TAB : tab)
const ACT_FLAG_VIEW_OPTIONS: Option[] = ACT_FLAG_OPTIONS.map((o) => ({ value: o.value, label: o.label }))

/** 업로드 파일 읽기 결과(파일 객체로 짝짓는다). */
/** errorIsRow — 특정 행을 짚는 파싱 오류(ExcelImportError.isRow)면 true(안내 기준: 행 짚는 검증은 알럿). */
type UploadParse = { file: File; rows: Partial<ReturnMonitoringItemDto>[]; error?: string; errorIsRow?: boolean }

type WithdrawPlan = { error: string } | { accepted: ReturnMonitoringRow[]; excluded: string[] }

function Rgr10V2({ coMingleEnabled }: CoMingleReturnProps) {
  // 계정은 마운트 시점 한 번 — 화주 전환은 reload 를 동반해 새 마운트를 만든다. 기준은 현재 화주(shipperCode, 전환 반영)다.
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르고 기본값은 MUSINSA 다. 고른 화주 그대로 조회한다(LOGISPRD-15725, `withCoMingle`).
  const accountRef = useRef<{ isFreeShipper: boolean; ownShipperCode: string; ownShipperName: string; scope: ShipperScope }>()
  if (!accountRef.current) {
    const user = AuthStorage.user.get()
    const scope = withCoMingle(shipperScopeOf(user), coMingleEnabled) // 잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652)
    accountRef.current = {
      isFreeShipper: !scope.locked,
      ownShipperCode: scope.shipperCode,
      ownShipperName: user.shipperName ? String(user.shipperName) : '',
      scope,
    }
  }
  const { isFreeShipper, ownShipperCode: accountShipperCode, ownShipperName: accountShipperName, scope } = accountRef.current
  /** 저장·철회의 화주 판정용 — 재고통합 ON 이면 세 화주를 MUSINSA 로 접는다(조회 경로 밖이라 그대로 둔다, LOGISPRD-15725) */
  const ownShipperCode = toReturnShipper(accountShipperCode, coMingleEnabled)
  /** 잠긴 계정이 고를 수 있는 화주 — 화주 목록 요청·옵션은 화주명이라 자기 화주는 세션 화주명이다(gr02V2 준용) */
  const selectable = useMemo(
    () => selectableShippers(scope).map((s) => ({ shipperCode: s.shipperCode, shipperName: s.shipperCode === accountShipperCode ? accountShipperName : s.shipperName })),
    [scope, accountShipperCode, accountShipperName],
  )
  const selectableNames = selectable.map((s) => s.shipperName).filter(Boolean)

  // LOGISPRD-12602 — 1.0 동등성 전환 플래그 2종. 조회 실패·로딩 중에는 false(as-is).
  const { data: isDateTypeFilterEnabled = false } = useFeatureFlag(RETURN_MONITORING_DATE_TYPE_FILTER_FLAG)
  const { data: isPartialWithdrawEnabled = false } = useFeatureFlag(RETURN_MONITORING_PARTIAL_WITHDRAW_FLAG)

  // ── 옵션 ──
  /** 화주 목록 — value=화주코드, label=화주명. 화주 고정 계정은 자기 화주 하나만(규칙 5). */
  const shipperQuery = useQuery<ShipperItem[]>({
    queryKey: ['rgr10', 'shippers', isFreeShipper ? 'ALL' : selectableNames.join(',')],
    queryFn: async () => {
      const res = await getShipperListV2({ shipperNames: isFreeShipper ? [] : selectableNames, logisticsServiceTypes: [], page: 0, size: 10000 })
      return res?.data?.items ?? []
    },
    enabled: isFreeShipper || selectableNames.length > 0,
    staleTime: API_COMMON_CACHE_TIME,
    meta: { noErrorMessage: true }, // 전역 QueryCache.onError 알럿을 끄고 아래 화면 문구만 낸다(두 번 뜨지 않게)
  })
  const allShippers = shipperQuery.data ?? []
  const shipperOptions = useMemo<Option[]>(() => {
    // 잠긴 계정은 고를 수 있는 화주만 — 자기 화주는 세션 화주명, 그 밖(MUSINSA)은 목록의 화주명을 라벨로 쓴다
    if (!isFreeShipper)
      return selectable.map((s) => ({
        value: s.shipperCode,
        label: (s.shipperCode === accountShipperCode ? s.shipperName : allShippers.find((a) => a.shipperCode === s.shipperCode)?.shipperName || s.shipperName) || s.shipperCode,
      }))
    const seen = new Set<string>()
    return allShippers.reduce<Option[]>((acc, s) => {
      const code = s.shipperCode
      if (!code || seen.has(code)) return acc
      seen.add(code)
      acc.push({ value: code, label: s.shipperName || code })
      return acc
    }, [])
  }, [isFreeShipper, allShippers, selectable, accountShipperCode])
  // 다운로드 사유코드 — 공용 콤보(DOWNLOAD_CD, D7). 데모 목데이터가 나가면 안 된다(규칙 7).
  const reasonQuery = useGetExportCombo({ noErrorMessage: true }) // 실패는 아래 usePboQueryErrorToast 가 한 번 — 전역 알림까지 뜨면 두 장 겹친다
  const { data: exportCombo } = reasonQuery
  const reasonOptions = useMemo<Option[]>(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  // 옵션 목록 실패 — 옵션이 비면 화주·사유를 못 고른다. 화면은 그리되 이유를 알린다(옛 화면은 빨간 안내, sy07·ma03 규약).
  //  다른 2.0 화면과 같은 훅 — 재조회 중엔 옛 실패를 알리지 않고, 재조회가 다시 실패하면 다시 알린다(#899 리뷰).
  usePboQueryErrorToast(shipperQuery, MESSAGES.shipperLoadFailed, { preferFallback: true })
  usePboQueryErrorToast(reasonQuery, MESSAGES.reasonLoadFailed, { preferFallback: true })

  // ── 조회조건 유지(메뉴 세션·URL, D16) ──
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<ReturnMonitoringSearchQuery>()
  // 마운트 시 한 번만 — 화주 고정 계정은 복원값이 고를 수 있는 화주가 아니면 기본 화주로(URL 은 사용자가 손댈 수 있다, 규칙 5).
  const [restored] = useState(() => {
    const r = fromSearchQuery(parsedQs as Record<string, unknown>)
    if (!r) return undefined
    return { ...r, values: forceOwnShipper(r.values, isFreeShipper ? r.values.shipper ?? '' : scopedShipperCode(scope, r.values.shipper)) }
  })

  // 조회에 실제로 쓴 조건 — null 이면 아직 조회 전이다.
  const [searchCondition, setSearchCondition] = useState<ReturnMonitoringFormValues | null>(restored?.values ?? null)
  const searchConditionRef = useRef(searchCondition)
  searchConditionRef.current = searchCondition
  const itemsFilter = useMemo(
    () => (searchCondition ? toReturnMonitoringReq(searchCondition, isDateTypeFilterEnabled) : null),
    [searchCondition, isDateTypeFilterEnabled],
  )
  const { isFetching, refetch } = useGetReturnMonitoringItems(itemsFilter)

  const rows = useReturnMonitoringRows()
  const rowsRef = useRef(rows.rows)
  rowsRef.current = rows.rows

  /** 조회 결과를 표에 싣는다 — 응답 효과로 덮지 않고 요청한 쪽이 직접 싣는다(같은 조건 재조회도 편집을 걷어 낸다, 옛 [조회] 동작). */
  const { setFromItems } = rows
  const load = useCallback(async () => {
    const { data, error } = await refetch({ cancelRefetch: false })
    if (error) {
      notice.failure(apiErrMessage(error, MESSAGES.searchFailed))
      return
    }
    setFromItems(data ?? [])
  }, [refetch, setFromItems])

  // URL 복원 조건이 있으면 진입 때 한 번 조회한다(옛 화면: 복원값이 있으면 자동 조회).
  const bootedRef = useRef(false)
  useEffect(() => {
    if (bootedRef.current || !restored) return
    bootedRef.current = true
    load().catch(() => undefined)
  }, [restored, load])

  /** 조회 상태를 메뉴 세션·URL 에 남긴다. reload() 를 빼면 탭 스트립이 옛 조건을 되살린다(D16). 같은 값이면 다시 쓰지 않는다. */
  const persistedRef = useRef('')
  // 활성 상태 탭 — 뷰가 onState.tab 으로 알린다(컨덕터 #696). 다운로드 범위(ordStatus)·URL 저장에 쓴다.
  const activeTabRef = useRef<StatusTabKey>(restored?.activeTab ?? ALL_STATUS_TAB)
  const persist = useCallback(
    (values: ReturnMonitoringFormValues, page: number, listQuery: string, tab: StatusTabKey) => {
      const query = toSearchQuery(values, tab, page, listQuery, isDateTypeFilterEnabled)
      const serialized = JSON.stringify(query)
      if (serialized === persistedRef.current) return
      persistedRef.current = serialized
      MenuStorage.menuOnSession.setSearch(query)
      menuSession.reload()
      replaceQuery(query)
    },
    [menuSession, replaceQuery, isDateTypeFilterEnabled],
  )

  // 업로드 파일 읽기 결과 — 모달 결과 한 줄과 [업로드] 가 같이 쓴다(파일 객체로 짝짓는다).
  const [uploadParse, setUploadParse] = useState<UploadParse | null>(null)
  const readingRef = useRef<File | null>(null)
  const readUpload = useCallback(async (file: File): Promise<UploadParse> => {
    try {
      const parsed = await parseReturnMonitoringExcel(file)
      return parsed.length === 0 ? { file, rows: [], error: MESSAGES.excelUploadEmpty } : { file, rows: parsed }
    } catch (e) {
      return { file, rows: [], error: e instanceof ExcelImportError ? e.message : MESSAGES.excelUploadUnreadable, errorIsRow: e instanceof ExcelImportError && e.isRow }
    }
  }, [])

  const saveMutation = useSaveReturnMonitoring()
  const withdrawMutation = useWithdrawReturnMonitoring()
  const [isDownloading, setIsDownloading] = useState(false)

  const viewRows = useMemo(() => rows.rows.map((row) => toViewRow(row, coMingleEnabled)), [rows.rows, coMingleEnabled])
  /** 선택 행 — 행 값은 편집 반영본(rows)에서 되찾는다(뷰가 넘긴 행은 표시용 사본이다). */
  const selectedRows = (ctx: ActionCtx): ReturnMonitoringRow[] => {
    const keys = new Set(ctx.selected.map(rowKeyOf))
    return rowsRef.current.filter((r) => keys.has(r._rowKey))
  }
  /** 저장 대상 화주 — 조회에 쓴 화주(조회 전 업로드만 했으면 계정 화주, 옛 화면과 같다). */
  const searchedShipper = (): string =>
    (searchConditionRef.current ? toReturnMonitoringReq(searchConditionRef.current, isDateTypeFilterEnabled).shipper : '') === ownShipperCode
      ? accountShipperCode
      : (searchConditionRef.current?.shipper || accountShipperCode)

  /** 반품철회 대상 판정 — 옛 handleWithdraw 의 검사 순서 그대로(상태 먼저, 화주 나중). */
  const planWithdraw = (targets: ReturnMonitoringRow[]): WithdrawPlan => {
    const isAccepted = (r: ReturnMonitoringRow) => (r.ordStatus ?? '').trim() === ACCEPTED_STATUS
    if (targets.length === 0) return { error: MESSAGES.withdrawNoSelection }
    // FF off — 접수완료가 아닌 행이 섞이면 전량 차단(as-is).
    if (!isPartialWithdrawEnabled && !targets.every(isAccepted)) return { error: MESSAGES.withdrawNotAccepted }
    // 화주 혼재는 FF 와 무관하게 전량 차단 — 철회 API 가 화주 단건만 받는다.
    // 통합 ON 에서는 원본 화주가 달라도 같은 MUSINSA 로 보이므로, 막는 까닭을 원본 화주 기준으로 알린다.
    if (new Set(targets.map((r) => r.sprNm)).size > 1) {
      return { error: coMingleEnabled ? '원본 화주가 다른 행이 섞여 있습니다. 원본 화주별로 나눠 반품철회해 주세요.' : MESSAGES.withdrawMultiShipper }
    }
    const accepted = targets.filter(isAccepted)
    if (accepted.length === 0) return { error: MESSAGES.withdrawNotAccepted }
    const excluded = targets.filter((r) => !isAccepted(r)).map((r) => r.orgInvoiceNo || MESSAGES.unknownOrgInvoiceNo)
    return { accepted, excluded }
  }

  /**
   * 반품철회 전송 — 접수완료만 보낸다(1.0 RGR10_DeleteForm 부분 처리, FF partialWithdraw). 실패는 안내 뒤 throw.
   * 서버가 건너뛴 행이 있으면 알럿으로 알리고 false(토스트 생략), 아니면 undefined(성공 토스트).
   */
  const sendWithdraw = async (plan: Extract<WithdrawPlan, { accepted: ReturnMonitoringRow[] }>): Promise<undefined | false> => {
    const ids = plan.accepted.map((r) => r.id).filter((id): id is number => id != null)
    if (ids.length === 0) throw invalidError(MESSAGES.withdrawNoSelection)
    let affected = 0
    try {
      const res = await withdrawMutation.mutateAsync({ shipperCode: plan.accepted[0]?.sprNm || ownShipperCode, ids })
      affected = res.data?.affectedRows ?? 0
    } catch (e) {
      throw failureError(apiErrMessage(e, MESSAGES.withdrawFailed))
    }
    if (searchConditionRef.current) await load()
    // 기준은 실제로 보낸 접수완료 행수다(옛 화면과 같다). 제외 행은 보내기 전 확인창에서 이미 알렸다.
    if (affected >= plan.accepted.length) return undefined
    notice.failure(MESSAGES.withdrawPartial(plan.accepted.length, affected))
    return false
  }

  // 화면관리 그리드 설정(LOGISPRD-17253) — 툴바·별표·[저장] 필수 검사가 RGR19_HEAD 를 따른다
  const permission = usePermissionSlot('rgr19', GRID_CDS)

  // ── 뷰 배선(Wiring) — 매 렌더 새로 만든다 ──
  const wiring: Wiring = {
    permission,
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { rgr10Table: coreTableLayoutSlot(GRID_CDS.rgr10Table) },
    rows: {
      // 전건이 한 번에 온다(서버 페이징 없음) — { rows, loading } 으로 로딩만 알린다(total 이 없으면 뷰가 클라이언트 페이징).
      ROWS: () => ({ rows: viewRows, loading: isFetching }),
    },
    actions: {
      /** [조회] — 화주·조회기간 필수. 조회를 안 했으면 false(뷰가 페이저·선택·검색칸·상태 탭을 되돌리지 않는다). */
      search: async (filters) => {
        const values = toFormValues(filters)
        const forced = isFreeShipper ? values : forceOwnShipper(values, scopedShipperCode(scope, values.shipper)) // 화주 잠금(규칙 5) — 고를 수 있는 화주, 아니면 기본 화주
        if (!hasRequiredFilter(forced)) {
          notice.invalid(MESSAGES.requiredFilter)
          return false
        }
        activeTabRef.current = ALL_STATUS_TAB
        persist(forced, 1, '', ALL_STATUS_TAB) // 조회하면 페이지·목록 검색어·탭은 처음으로(뷰도 되돌린다 — 1.0 onHandleSearch)
        flushSync(() => setSearchCondition(forced))
        await load()
      },

      /**
       * 편집 셀 — 표가 돌려준 행을 key 로 찾아 편집 셀만 되돌린다. 바코드가 바뀐 행의 SKUID 비우기는 옛 훅이 한다.
       * ⚠️ DS 표(0.8.2)는 이 콜백을 자기 setState 업데이터 안에서 부른다 — 곧바로 상태를 바꾸면 무한 갱신이 난다(rgr04 실측).
       *  한 틱 미루고, 값이 실제로 바뀐 행만 반영한다. 표가 넘기는 행은 편집 누적본이라 마지막 호출이 앞선 편집을 모두 담는다.
       */
      ROWS_rowsChange: (changed) => {
        const byKey = new Map(changed.map((r) => [rowKeyOf(r), r]))
        queueMicrotask(() => {
          const merged = rowsRef.current.flatMap((r) => {
            const view = byKey.get(r._rowKey)
            if (!view) return []
            const next = fromViewRow(view, r)
            return EDITABLE_ENTRIES.some(([, field]) => next[field] !== r[field]) ? [next] : []
          })
          if (merged.length > 0) rows.onRowsChange(merged)
        })
      },

      /**
       * [저장] — 고른 행 중 편집분만 I/E 로 보낸다(옛 handleSave). 확인창은 옛 화면 그대로. 검증·API 실패는 안내 뒤 throw(선택 유지).
       * 서버 안내(건너뛴 원송장·원출고 미매칭)가 있으면 알럿으로 보이고 토스트는 생략한다(LLD 3.3 — 반드시 보여야 하는 값).
       */
      rgr10SaveButton: async (ctx) => {
        // 받는 중이면 곧 덮일 행이다 — throw 로 막아 선택을 남긴다(안내는 guardFetching 이 띄운다)
        if (!guardFetching(isFetching)) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const { payload: built, errors } = rows.buildSavePayload(
          selectedRows(ctx).map((r) => r._rowKey),
          toRequiredFields(permission.forTable('rgr10Table').columns(REQUIRED_CANDIDATES)),
        )
        if (errors.length > 0) throw invalidError(errors[0])
        // 통합 조회에서는 기존 행의 원본 화주가 최상위 화주와 다를 수 있다. 행 화주로 실어 BE 가 원본 화주로 찾게 한다.
        const sprNmById = new Map(rowsRef.current.map((r) => [r.id, r.sprNm]))
        const payload = coMingleEnabled
          ? built.map((row) => {
              const shipperCode = row.edited === 'E' && !row.shipperCode && row.id != null ? sprNmById.get(row.id) : null
              return shipperCode ? { ...row, shipperCode } : row
            })
          : built
        // LOGISPRD-12614 — 비는 까닭이 둘이라 안내도 둘. 편집한 행을 대신 골라 주지는 않는다(E2E W-01).
        if (payload.length === 0) throw invalidError(rows.hasDirtyRows() ? MESSAGES.saveNoSelection : MESSAGES.saveNoEdits)
        await confirmOrThrow(MESSAGES.saveConfirm(payload.length))
        const lines: string[] = []
        try {
          const { data } = await saveMutation.mutateAsync({ shipperCode: searchedShipper(), rows: payload })
          if (data?.message) lines.push(data.message)
          if (data?.duplicateOrgInvoiceNos?.length) lines.push(`중복 원송장으로 건너뛴 원송장번호: ${data.duplicateOrgInvoiceNos.join(', ')}`)
          if (data?.sourceNotFound?.length) lines.push(`원출고를 찾지 못한 원송장번호: ${data.sourceNotFound.join(', ')}`)
        } catch (e) {
          // 화주 쓰기 범위 밖(403 SHIPPER_SCOPE_FORBIDDEN)은 화면 문구로 바꾼다. 상태 403 만 보면 멤버십 차단 403(전역 알럿·로그아웃)까지
          // 잡혀 안내가 겹친다. '권한'·'화주' 키워드는 옛 화면과 같은 느슨한 판정이다.
          const raw = apiErrMessage(e, MESSAGES.saveFailed)
          const isShipperDenied = Utils.getApiErrorCode(e) === 'SHIPPER_SCOPE_FORBIDDEN'
          throw failureError(isShipperDenied || raw.includes('권한') || raw.includes('화주') ? MESSAGES.saveOwnershipDenied : raw)
        }
        // 저장은 끝났다 — 재조회 실패를 저장 실패로 오인시키지 않는다(load 가 따로 알린다).
        if (searchConditionRef.current) await load()
        if (lines.length === 0) return undefined
        notice.failure(lines.join('\n'))
        return false
      },

      /** [반품철회] 확인(생성본 확인창) — 접수완료만 고른 경우다. 제외 행이 섞이면 opens 가 옛 확인창으로 따로 돌린다. */
      rgr10WithdrawButton_ok: async (ctx) => {
        const plan = planWithdraw(selectedRows(ctx))
        if ('error' in plan) throw invalidError(plan.error)
        return sendWithdraw(plan)
      },

      /** 엑셀 다운로드 — 사유·비밀번호 검증(옛 모달의 [확인] 잠금 조건) 뒤 서버가 조회 조건으로 다시 뽑는다(화면 목록 검색과 무관). */
      rgr10DownloadModal_ok: async (ctx) => {
        const reasonCode = asText(ctx.form?.reason)
        const reasonDetail = asText(ctx.form?.detail).trim()
        const password = asText(ctx.form?.password)
        if (!reasonCode) throw invalidError(MESSAGES.downloadReasonRequired)
        if (reasonDetail.length < REASON_DETAIL_MIN_LENGTH) throw invalidError(MESSAGES.reasonDetailHelp)
        if (!isValidDownloadPassword(password)) throw invalidError(MESSAGES.passwordRuleHelp)
        const condition = searchConditionRef.current
        if (!condition) throw invalidError(MESSAGES.excelDownloadRequiresSearch)
        setIsDownloading(true)
        try {
          // 보고 있는 상태 탭 범위만 받는다(옛 handleDownloadConfirm — '전체' 면 ordStatus 를 싣지 않는다).
          const tab = activeTabRef.current
          await downloadReturnMonitoringExcel({
            ...toReturnMonitoringReq(condition, isDateTypeFilterEnabled),
            ...(tab === ALL_STATUS_TAB ? {} : { ordStatus: tab }),
            password,
            detailedReason: `${reasonCode}_${reasonDetail}`,
          })
        } catch (e) {
          throw failureError(apiErrMessage(e, MESSAGES.excelDownloadFailed))
        } finally {
          setIsDownloading(false)
        }
      },

      /** 엑셀 업로드 — 클라이언트가 파싱해 신규 행(I)으로 맨 위에 얹는다(1.0 TOOL_IMP, 업로드 API 없음). 전 행 선택은 selectAllAfterUpload. */
      rgr10UploadModal_ok: async (ctx) => {
        const file = ctx.form?.file
        if (!(file instanceof File)) throw invalidError(MESSAGES.excelUploadNoFile)
        // 파일을 고를 때 이미 읽었으면 그 결과를 쓴다(같은 파일을 두 번 읽지 않는다).
        const known = uploadParse?.file === file ? uploadParse : await readUpload(file)
        if (known.error) throw known.errorIsRow ? rowInvalidError(known.error) : invalidError(known.error)
        const parsed = known.rows
        rows.addUploadedRows(parsed)
        callToAddImportLog({ menuCode: 'RGR19', apiName: 'RGR19_LIST_IMPORT', rowCount: parsed.length })
        noticeIfUploadExceedsPage(parsed.length)
        return MESSAGES.excelUploaded(parsed.length)
      },
    },
    options: {
      RGR10SHIPPER_OPTIONS: () => shipperOptions,
      RGR10DATE_TYPE_OPTIONS: () => DATE_TYPE_VIEW_OPTIONS,
      RGR10ACTFLAG_OPTIONS: () => ACT_FLAG_VIEW_OPTIONS,
      REASONRADIO_OPTIONS: () => reasonOptions,
    },
    // 기본값 — 화주 = 계정 화주(옛 buildDefaultValues, 재고통합 대상 계정은 MUSINSA — `defaultShipper`). 기간은 뷰의 기본 기간(최근 일주일 — 옛 기본값과 같다).
    /**
     * 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·search 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 칸을 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15725)
     */
    filterDisabled: isFreeShipper ? undefined : { SPR_NM: !canSelectShipper(scope) },
    filterDefaults: { rgr10FilterCard: (demo) => ({ ...demo, SPR_NM: defaultShipper(scope).shipperCode, rgr10DateType: DEFAULT_DATE_TYPE }) },
    filterInitial: { rgr10FilterCard: (defaults) => (restored ? { ...defaults, ...toFilterValues(restored.values) } : defaults) },
    stateInitial: restored
      ? { pager: { pager: { page: restored.page } }, listq: { 'table.q': restored.listQuery }, tab: { rgr10Status: toViewTab(restored.activeTab) } }
      : undefined,
    /** 페이지 이동·목록 검색어 확정·상태 탭 전환 — 탭은 늘 기억하고, URL/세션은 조회한 적이 있을 때만 싣는다(옛 화면과 같다). */
    onState: (s) => {
      activeTabRef.current = toStatusTab(s.tab.rgr10Status)
      if (!searchConditionRef.current) return
      persist(searchConditionRef.current, s.pager.pager.page, s.listq['table.q'], activeTabRef.current)
    },
    /** 업로드 모달 결과 한 줄(컨덕터 #696 parseResult) — 고른 파일을 읽은 결과를 폼 버스 parsed·parseError 로 낸다(옛 ExcelUploadModal 동작). */
    form: {
      rgr10UploadModal: (state) => {
        const file = state.file instanceof File ? state.file : null
        if (!file) return state
        if (uploadParse?.file !== file) {
          // 렌더 중 상태를 바꾸지 않는다 — 읽기는 한 틱 뒤에 시작하고 끝나면 결과를 싣는다.
          if (readingRef.current !== file) {
            readingRef.current = file
            queueMicrotask(() => {
              // readUpload 는 실패도 결과({ error })로 돌려줘 거부되지 않는다 — catch 는 떠 있는 프로미스 표시용(레포 no-void, load() 와 같은 형식).
              readUpload(file)
                .then((r) => setUploadParse(r))
                .catch(() => undefined)
            })
          }
          return state
        }
        return uploadParse.error
          ? { ...state, parseError: uploadParse.error }
          : { ...state, parsed: MESSAGES.excelRecognized(uploadParse.rows.length) }
      },
    },
    /** [초기화] — 저장된 조건·결과·선택까지 지운다(옛 handleReset). 조회는 하지 않는다. */
    onReset: () => {
      setSearchCondition(null)
      rows.clear()
      persistedRef.current = ''
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
    },
    opens: {
      /**
       * 막히는 선택이면 확인창을 열지 않고 까닭을 알린다(옛 handleWithdraw 의 사전 검사).
       * 접수완료가 아닌 행이 섞였으면(FF partialWithdraw) 생성본 확인창 대신 옛 확인창으로 **보내기 전에** 제외 건·원송장번호를 보여 준다 —
       *  생성본 확인창 문구는 고정('선택한 N건')이라 제외 안내를 실을 자리가 없다. TODO(dev): 확인 문구 슬롯이 생기면 생성본 확인창으로 되돌린다.
       *  이 경로는 뷰의 선택 비우기를 거치지 않는다 — 재조회 뒤에도 같은 행키면 체크가 남는다.
       */
      rgr10WithdrawButton: (ctx) => {
        if (!guardFetching(isFetching)) return false
        const plan = planWithdraw(selectedRows(ctx))
        if ('error' in plan) {
          notice.invalid(plan.error)
          return false
        }
        if (plan.excluded.length === 0) return true
        PboConfirm.show(MESSAGES.withdrawConfirmPartial(plan.accepted.length, plan.excluded), {
          onConfirm: async () => {
            try {
              if ((await sendWithdraw(plan)) !== false) notice.success(MESSAGES.withdrawSuccess)
            } catch {
              // 실패 안내는 sendWithdraw 가 이미 띄웠다
            }
          },
        })
        return false
      },
      // 행수 상한(10만)은 선차단하지 않는다 — 서버가 초과 메시지를 그대로 준다(피드백 13).
      rgr10ExcelButton: () => {
        if (!guardFetching(isFetching)) return false
        if (rowsRef.current.length === 0) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        if (!searchConditionRef.current) {
          notice.invalid(MESSAGES.excelDownloadRequiresSearch)
          return false
        }
        return true
      },
    },
    // 1.0 동작 — 업로드 직후 보이는 행을 전부 선택해 바로 [저장] 하게 한다.
    selectAllAfterUpload: true,
    // 요청 중이면 선택 게이트 버튼(저장·반품철회)을 잠근다 — 중복 요청 방지(규칙 11).
    useBusy: () => saveMutation.isPending || withdrawMutation.isPending || isDownloading,
  }

  return <ReturnMonitoringPageView wiring={wiring} />
}

export default withCoMingleReturns(Rgr10V2)
